import { useMemo, useState } from "react";
import { SortColumn } from "react-data-grid";

import { ColorConstants } from "../../../../constants";
import { CursorChangeProps, ResultType } from "../../../../models";
import { getProjectIssuances } from "../../../../service/publicquery";
import {
  getHabitatFromStandardSpecificIssuanceData,
  getInventoryColumns,
  mapInventoryIssuancesToRows,
} from "../../../../utils";
import {
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  dataGridMapFilterCriteria,
  StackedChartData,
} from "../../../../widget";
import { usePublicContext } from "../../usePublicContext";

interface UseInventoryReturnData {
  onChange: DataGridCursorDataLoadEventHandler;
  isLoading: boolean;
  hasProjectDetails: boolean;
  hasData: boolean;
  hasEnoughIssuancesForScrolling: boolean;
  columns: DataGridColumnDefinition[];
  unitMeasurement: string;
  stackedChartData: StackedChartData[] | undefined;
  showAvailable: boolean;
  xAxisLabels: (string | string[])[];
  defaultSortingCriteria: SortColumn[];
}

const getDefaultSortingCriteria = (unitMeasurement: string): SortColumn[] => {
  if (unitMeasurement === "Biodiversity") {
    return [{ columnKey: "issuanceDate", direction: "ASC" }];
  }
  return [{ columnKey: "vintage", direction: "ASC" }];
};

export const useInventory = (): UseInventoryReturnData => {
  const { projectDetails } = usePublicContext();
  const showPrice = !!projectDetails && projectDetails.listing?.showPriceInDirectory;
  const showAvailable = !!projectDetails && projectDetails.listing?.availableForSale;
  const [isLoading, setIsLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [hasEnoughIssuancesForScrolling, setHasEnoughIssuancesForScrolling] = useState(false);

  const [stackedChartData, setStackedChartData] = useState<StackedChartData[]>();
  const [xAxisLabels, setXAxisLabels] = useState<(string | string[])[]>([]);

  const unitMeasurement = useMemo(
    () => projectDetails?.unitMeasurements?.[0] ?? "Carbon",
    [projectDetails?.unitMeasurements]
  );

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    const filterCriteria = dataGridMapFilterCriteria(filtering);
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
    if (!projectDetails) {
      return data;
    }
    setIsLoading(true);

    const sortKeyMap: Record<string, string> = {
      vintage: "vintageStartDate",
    };

    await getProjectIssuances({
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: paging.pageSize,
      },
      /* eslint-disable @typescript-eslint/no-explicit-any */
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
      /* eslint-enable */
      filter: { results: filterCriteria },
      projectUuid: projectDetails?.uuid,
    })
      .then((response) => {
        const results = response.data?.results ?? [];
        data = {
          resultData: mapInventoryIssuancesToRows(results, unitMeasurement),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };

        const keys: (string | string[])[] = [];
        const verifiedTotal: number[] = [];
        const verifiedAvailable: number[] = [];
        const pendingTotal: number[] = [];
        const pendingAvailable: number[] = [];

        switch (unitMeasurement) {
          case "Biodiversity": {
            const groupedResults: {
              [key: string]: {
                unitQuantityTotal: number;
                unitQuantityAvailable: number;
              };
            } = {};

            results.forEach((value) => {
              const habitatType = getHabitatFromStandardSpecificIssuanceData(value.standardSpecificIssuanceData).join(
                "-"
              );

              if (!groupedResults[habitatType]) {
                groupedResults[habitatType] = {
                  unitQuantityTotal: 0,
                  unitQuantityAvailable: 0,
                };
              }

              groupedResults[habitatType].unitQuantityTotal += value.unitQuantityTotal;
              groupedResults[habitatType].unitQuantityAvailable += value.unitQuantityAvailable;
            });

            Object.keys(groupedResults).forEach((key: string) => {
              keys.push(key.split("-"));
              verifiedTotal.push(groupedResults[key].unitQuantityTotal);
              if (showAvailable) {
                verifiedAvailable.push(groupedResults[key].unitQuantityAvailable);
              }
            });
            break;
          }
          case "Carbon":
          default: {
            results.forEach((value) => {
              keys.push(`${value.vintageStartDate?.getFullYear()}-${value.vintageEndDate?.getFullYear()}`);
              const isVerified = value.verificationStatus === "Verified";
              verifiedTotal.push(isVerified ? value.unitQuantityTotal : 0);
              pendingTotal.push(isVerified ? 0 : value.unitQuantityTotal);
              if (showAvailable) {
                verifiedAvailable.push(isVerified ? value.unitQuantityAvailable : 0);
                pendingAvailable.push(isVerified ? 0 : value.unitQuantityAvailable);
              }
            });
            break;
          }
        }

        setHasData(results.length > 0);
        setHasEnoughIssuancesForScrolling(results.length > 5);
        setXAxisLabels(keys);

        const pendingTotalChart = pendingTotal.map((total, i) =>
          showAvailable && total === pendingAvailable[i] ? 0 : total - (showAvailable ? pendingAvailable[i] : 0)
        );

        const verifiedTotalChart = verifiedTotal.map((total, i) =>
          showAvailable && total === verifiedAvailable[i] ? 0 : total - (showAvailable ? verifiedAvailable[i] : 0)
        );

        setStackedChartData([
          {
            label: "Allocated",
            data: pendingTotalChart,
            backgroundColor: ColorConstants.INDIGO_500,
            hoverBackgroundColor: ColorConstants.INDIGO_400,
          },
          {
            label: "Avaliable",
            data: pendingAvailable,
            backgroundColor: ColorConstants.INDIGO_100,
            hoverBackgroundColor: ColorConstants.BLUE_100,
          },
          {
            label: "Allocated",
            data: verifiedTotalChart,
            backgroundColor: ColorConstants.GREEN_600,
            hoverBackgroundColor: ColorConstants.GREEN_500,
          },
          {
            label: "Avaliable",
            data: verifiedAvailable,
            backgroundColor: ColorConstants.GREEN_200,
            hoverBackgroundColor: ColorConstants.GREEN_100,
          },
        ]);
      })
      .finally(() => {
        setIsLoading(false);
      });

    return data;
  };

  return {
    onChange,
    isLoading,
    hasData,
    hasEnoughIssuancesForScrolling,
    hasProjectDetails: !!projectDetails,
    columns: getInventoryColumns(unitMeasurement, showPrice, showAvailable),
    unitMeasurement,
    xAxisLabels,
    showAvailable,
    stackedChartData,
    defaultSortingCriteria: getDefaultSortingCriteria(unitMeasurement),
  };
};
